import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import ConfirmDialog from "../../../components/ConfirmDialog";
import Notification from "../../../components/Notification";
import { apiCall, formatDay, formatDayTime } from "../../../utils/apiCall";
import { DeadlineNote, reminderLabel, StatusBadge, STATUSES } from "./issueBits";

// Notes the API writes by itself, not typed by anyone.
const SYSTEM_NOTES = ["Logged", "Details edited", "Notifications stopped", "Notifications resumed"];

const initials = (name) => (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

// "5 minutes ago", "3 hours ago", "2 days ago"; nothing beyond a week (the date says it).
const timeAgo = (value) => {
    const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
    if (Number.isNaN(minutes) || minutes < 0) return "";
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    const days = Math.round(hours / 24);
    return days <= 7 ? `${days} day${days === 1 ? "" : "s"} ago` : "";
};

// One entry in the history: who did what, when, and the note they wrote.
function HistoryEntry({ entry }) {
    const who = entry.by || "Someone";
    const typedNote = entry.note && !SYSTEM_NOTES.includes(entry.note) ? entry.note : "";

    let action;
    if (!entry.status_from && entry.status_to) action = <>logged this issue as <StatusBadge status={entry.status_to} /></>;
    else if (entry.status_from && entry.status_to) action = <>changed the status from <StatusBadge status={entry.status_from} /> to <StatusBadge status={entry.status_to} /></>;
    else if (entry.note === "Details edited") action = "edited the details";
    else if (entry.note === "Notifications stopped") action = "stopped the notifications for this issue";
    else if (entry.note === "Notifications resumed") action = "resumed the notifications for this issue";
    else action = "added a progress note";

    const ago = timeAgo(entry.created_at);

    return (
        <li className="cf-history-entry">
            <span className="cf-history-avatar" aria-hidden="true">{initials(who)}</span>
            <div className="cf-history-body">
                <p className="cf-history-line"><strong>{who}</strong> {action}</p>
                <p className="cf-history-when">
                    <time dateTime={entry.created_at}>{formatDayTime(entry.created_at)}</time>
                    {ago && <> &middot; {ago}</>}
                </p>
                {typedNote && <blockquote className="cf-history-note">{typedNote}</blockquote>}
            </div>
        </li>
    );
}

// One issue: its details, the people responsible, a form to change the status or
// add a progress note, and everything that has happened to it. Admins can also edit
// or delete it; the members responsible can report progress and close it.
function IssueView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [issue, setIssue] = useState(null);
    const [notice, setNotice] = useState(location.state?.saved ? { type: "success", message: "Issue saved. The members responsible have been notified." } : null);
    const [status, setStatus] = useState("");
    const [note, setNote] = useState("");
    // How whoever logged the issue and the members responsible are told about the update.
    const [tell, setTell] = useState({ email: true, sms: true });
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [confirmPost, setConfirmPost] = useState(false);
    const [confirmNotifications, setConfirmNotifications] = useState(false);
    const [switching, setSwitching] = useState(false);

    const load = useCallback(async () => {
        const { ok, status: code, data } = await apiCall(`/issues/${id}`);
        if (code === 401) return navigate("/logout", { state: { from: location } });
        if (!ok) return setNotice({ type: "danger", message: data?.message || "The issue could not be loaded." });
        setIssue(data);
        setStatus(data.status);
        document.title = `${data.title} - Issue Log - CFCM Portal`;
    }, [id, navigate, location]);

    useEffect(() => { load(); }, [load]);

    // "... are sent an email and an SMS about it." for the confirmation dialog.
    const howTold = () => {
        const ways = [tell.email && "an email", tell.sms && "an SMS"].filter(Boolean);
        return ways.length
            ? `whoever logged the issue and the members responsible are sent ${ways.join(" and ")} about it (and see it under their notifications).`
            : "whoever logged the issue and the members responsible only see it under their notifications in the portal (no email or SMS).";
    };

    // The form asks first; the update is only posted once confirmed.
    const askToPost = (event) => {
        event.preventDefault();
        setConfirmPost(true);
    };

    const saveStatus = async () => {
        setSaving(true);
        try {
            const { ok, data } = await apiCall(`/issues/${id}/status`, { method: "POST", body: { status, note, notify_email: tell.email, notify_sms: tell.sms } });
            if (!ok) return setNotice({ type: "danger", message: data?.message || "The update could not be saved." });
            setIssue(data);
            setNote("");
            setNotice({ type: "success", message: status === "Closed" && issue.status !== "Closed" ? "Issue closed. No more reminders will be sent for it." : "Update saved." });
        } finally {
            setSaving(false);
            setConfirmPost(false);
        }
    };

    // Stop or resume the automatic reminders (weekly list and "due in 3 days").
    const switchNotifications = async () => {
        setSwitching(true);
        try {
            const stop = !issue.notifications_stopped;
            const { ok, data } = await apiCall(`/issues/${id}/notifications`, { method: "POST", body: { stopped: stop } });
            if (!ok) return setNotice({ type: "danger", message: data?.message || "That did not work. Please try again." });
            setIssue(data);
            setNotice({ type: "success", message: stop ? "Notifications stopped. No reminders will be sent for this issue until they are resumed." : "Notifications resumed. Reminders will be sent for this issue again." });
        } finally {
            setSwitching(false);
            setConfirmNotifications(false);
        }
    };

    const remove = async () => {
        const { ok, data } = await apiCall(`/issues/${id}`, { method: "DELETE" });
        setConfirmDelete(false);
        if (ok) navigate("/dashboard/issues");
        else setNotice({ type: "danger", message: data?.message || "The issue could not be deleted." });
    };

    if (!issue) {
        return <main>{notice ? <Notification type={notice.type} message={notice.message} onClose={() => setNotice(null)} /> : <p>Loading...</p>}</main>;
    }

    const changed = status !== issue.status || note.trim() !== "";

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <div>
                            <p className="title is-4">{issue.title}</p>
                            <p><StatusBadge status={issue.status} /> &nbsp; <span className="cf-muted">{issue.type_name}</span></p>
                        </div>
                    </div>
                </div>
                <div className="level-right">
                    <p className="level-item">
                        <Link to="/dashboard/issues" className="button primary-bg"><i className="fa fa-arrow-left" aria-hidden="true"></i> &nbsp; Back</Link>
                        {issue.status !== "Closed" && (
                            <>
                                &nbsp;&nbsp;
                                <button type="button" className="button primary-outline-button" onClick={() => setConfirmNotifications(true)}>
                                    <i className={`fa ${issue.notifications_stopped ? "fa-bell" : "fa-bell-slash"}`} aria-hidden="true"></i> &nbsp; {issue.notifications_stopped ? "Resume notifications" : "Stop notifications"}
                                </button>
                            </>
                        )}
                        {issue.can_manage && (
                            <>
                                &nbsp;&nbsp;
                                <Link to={`/dashboard/issues/${id}/edit`} className="button primary-bg"><i className="fa fa-pencil" aria-hidden="true"></i> &nbsp; Edit</Link>
                                &nbsp;&nbsp;
                                <button type="button" className="button is-danger is-light" onClick={() => setConfirmDelete(true)}>Delete</button>
                            </>
                        )}
                    </p>
                </div>
            </nav>

            {notice && <Notification type={notice.type} message={notice.message} onClose={() => setNotice(null)} />}

            <div className="columns is-multiline">
                <div className="column is-7">
                    <div className="box">
                        <dl className="cf-history-facts">
                            <div><dt>Deadline</dt><dd>{issue.deadline ? formatDay(issue.deadline) : "None"}<br /><span className="is-size-7"><DeadlineNote issue={issue} /></span></dd></div>
                            <div><dt>Logged</dt><dd>{formatDay(issue.created_at)}{issue.created_by ? <><br /><span className="is-size-7 cf-muted">by {issue.created_by.name}</span></> : null}</dd></div>
                            <div><dt>Reminders</dt><dd>{issue.notifications_stopped ? "Stopped" : reminderLabel(issue.reminder_frequency)}{issue.notifications_stopped && issue.notifications_stopped_by ? <><br /><span className="is-size-7 cf-muted">by {issue.notifications_stopped_by}</span></> : null}{issue.last_reminded_at ? <><br /><span className="is-size-7 cf-muted">last sent {formatDay(issue.last_reminded_at)}</span></> : null}</dd></div>
                            {issue.closed_at && <div><dt>Closed</dt><dd>{formatDay(issue.closed_at)}{issue.closed_by ? <><br /><span className="is-size-7 cf-muted">by {issue.closed_by}</span></> : null}</dd></div>}
                        </dl>

                        <p className="title is-6 mb-2">Responsible</p>
                        {issue.assignees.length ? (
                            <ul className="cf-issue-people">
                                {issue.assignees.map((a) => (
                                    <li key={a.id}>
                                        {a.user_id && issue.can_manage ? <Link to={`/dashboard/members/${a.user_id}/details`}>{a.name}</Link> : a.name}
                                        {!a.is_member && <span className="cf-muted is-size-7"> (not a member of the portal)</span>}
                                    </li>
                                ))}
                            </ul>
                        ) : <p className="cf-muted">No one yet.{issue.can_manage && <> <Link to={`/dashboard/issues/${id}/edit`}>Add someone</Link>.</>}</p>}

                        {issue.description && (
                            <>
                                <p className="title is-6 mt-4 mb-2">Details</p>
                                <p className="cf-issue-text">{issue.description}</p>
                            </>
                        )}
                    </div>
                </div>

                <div className="column is-5">
                    <form className="box" onSubmit={askToPost}>
                        <p className="title is-6">Update</p>
                        <div className="field">
                            <label className="label" htmlFor="issue-status">Status</label>
                            <div className="select is-fullwidth">
                                <select id="issue-status" value={status} onChange={(e) => setStatus(e.target.value)}>
                                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            {status === "Closed" && issue.status !== "Closed" && <p className="help">Closing stops the reminders for this issue.</p>}
                        </div>
                        <div className="field">
                            <label className="label" htmlFor="issue-note">Progress note (optional)</label>
                            <textarea id="issue-note" className="textarea" rows="3" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Quote received, payment sent, waiting for delivery..." maxLength={2000} />
                        </div>
                        <fieldset className="field">
                            <legend className="label">Tell the people involved by</legend>
                            <label className="checkbox mr-4">
                                <input type="checkbox" checked={tell.email} onChange={(e) => setTell({ ...tell, email: e.target.checked })} /> &nbsp;Email
                            </label>
                            <label className="checkbox">
                                <input type="checkbox" checked={tell.sms} onChange={(e) => setTell({ ...tell, sms: e.target.checked })} /> &nbsp;SMS
                            </label>
                            <p className="help">Whoever logged the issue and the members responsible. They always see it under their notifications in the portal.</p>
                        </fieldset>
                        <button type="submit" className={`button primary-bg is-fullwidth ${saving ? "is-loading" : ""}`} disabled={!changed || saving}>Save update</button>
                    </form>
                </div>

                <div className="column is-12">
                    <div className="box">
                        <p className="title is-6">History</p>
                        <p className="cf-muted is-size-7 mb-3">Newest first.</p>
                        <ol className="cf-issue-timeline">
                            {issue.updates.map((u) => <HistoryEntry key={u.id} entry={u} />)}
                        </ol>
                    </div>
                </div>
            </div>

            <ConfirmDialog
                isOpen={confirmNotifications}
                theme={issue.notifications_stopped ? "is-primary" : "is-warning"}
                title={issue.notifications_stopped ? "Resume Notifications" : "Stop Notifications"}
                message={issue.notifications_stopped
                    ? `Are you sure you want to resume the notifications for "${issue.title}"?`
                    : `Are you sure you want to stop the notifications for "${issue.title}"?`}
                note={issue.notifications_stopped
                    ? `When resumed, the reminders start again: ${reminderLabel(issue.reminder_frequency).toLowerCase()} while the issue needs attention, and 3 days before the due date.`
                    : "When stopped, no reminders are sent for this issue: neither the regular one nor the one 3 days before the due date. Updates someone posts are still sent as they choose. You can resume them at any time."}
                onConfirm={switchNotifications}
                onCancel={() => setConfirmNotifications(false)}
                isLoading={switching}
            />

            <ConfirmDialog
                isOpen={confirmPost}
                theme={status === "Closed" && issue.status !== "Closed" ? "is-success" : "is-primary"}
                title="Post Update"
                message={status !== issue.status
                    ? `Are you sure you want to change the status of "${issue.title}" from ${issue.status} to ${status}?`
                    : `Are you sure you want to post this progress note on "${issue.title}"?`}
                note={`When posted, it is added to the history under your name with today's date and time, and ${howTold()} It cannot be edited or removed afterwards.${status === "Closed" && issue.status !== "Closed" ? " Closing also stops the reminders for this issue." : ""}`}
                onConfirm={saveStatus}
                onCancel={() => setConfirmPost(false)}
                isLoading={saving}
            >
                {note.trim() && <blockquote className="cf-history-note mt-3">{note.trim()}</blockquote>}
            </ConfirmDialog>

            <ConfirmDialog
                isOpen={confirmDelete}
                theme="is-danger"
                title="Delete Issue"
                message={`Are you sure you want to delete "${issue.title}"?`}
                note="When deleted, the issue and its history are removed for good, and no more reminders are sent. To keep a record, close it instead."
                onConfirm={remove}
                onCancel={() => setConfirmDelete(false)}
            />
        </main>
    );
}

export default IssueView;
